/*
 * Cyphon accessories (sections/cy-accessories.liquid): category tabs and their feature cards.
 * Tabs follow the WAI-ARIA tabs pattern (click, arrow keys, Home, End) and follow theme editor block selection.
 * Card swap: GSAP (loaded once near the viewport) fades the old copy out, then wipes the new card in; the CSS
 * keyframes in assets/cy-accessories.css run until GSAP arrives; no motion with reduced motion. The entrance runs once from an
 * IntersectionObserver; there is no scroll listener. Only clip-path, transform and opacity animate.
 */
if (!customElements.get('cy-accessories')) {
  const reduce = () => matchMedia('(prefers-reduced-motion:reduce)').matches;
  // Phones, tablets and low-memory devices: a short opacity fade. A clip-path wipe on a full-bleed photo is dropped
  // there, so the new picture waited for its download and then popped in.
  const lite = () => matchMedia('(max-width:989px)').matches || (navigator.deviceMemory && navigator.deviceMemory <= 4);
  const gsapSrc = document.currentScript?.dataset.gsap;
  // Load GSAP once when the section nears the viewport (reuses the hero's copy if it is already on the page)
  const loadGsap = () => {
    if (window.gsap || !gsapSrc || document.querySelector('script[src="' + gsapSrc + '"]')) return;
    const s = document.createElement('script');
    s.src = gsapSrc;
    s.async = true;
    document.head.appendChild(s);
  };

  class CyAccessories extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-ac-list]');
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.current = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));

      if (this.list) {
        this.list.addEventListener('click', (e) => {
          const t = e.target.closest('[role="tab"]');
          if (t) this.select(this.tabs.indexOf(t));
        });
        this.list.addEventListener('keydown', (e) => this.onKey(e));
      }
      // Theme editor: selecting a Category block shows its card
      this.addEventListener('shopify:block:select', (e) => {
        const i = this.tabs.indexOf(e.target);
        if (i > -1) this.select(i, false, true);
      });

      // Desktop liquid-glass highlight: follows the active tab and re-measures when the bar resizes
      this.ind = this.querySelector('.cy-ac__ind');
      if (this.ind && this.list) this.list.classList.add('has-ind');
      if (this.list && 'ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.moveInd());
        this.ro.observe(this.list);
      }
      this.moveInd();
      this.bgLayers = [...this.querySelectorAll('.cy-ac__bgx')];
      // preload every tab photo now, so a click never waits for a download
      this.imgCache = new Map();
      this.tabs.forEach((t, k) => {
        [this.panels[k]?.dataset.bg, this.panels[k]?.dataset.bgm].forEach((u) => {
          if (u && !this.imgCache.has(u)) {
            const im = new Image();
            im.decoding = 'async';
            im.src = u;
            this.imgCache.set(u, im);
          }
        });
      });
      this.setBg(this.panels[this.current]);

      if (!reduce() && 'IntersectionObserver' in window) {
        this.gio = new IntersectionObserver(
          ([e]) => {
            if (!e.isIntersecting) return;
            this.gio.disconnect();
            if (!lite()) loadGsap();
          },
          { rootMargin: '400px 0px' }
        );
        this.gio.observe(this);
      }

      this.reveal();
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.gio?.disconnect();
      this.ro?.disconnect();
      clearTimeout(this.onTimer);
      this.finish?.();
    }

    onKey(e) {
      const n = this.tabs.length;
      const keys = { ArrowRight: this.current + 1, ArrowLeft: this.current - 1, Home: 0, End: n - 1 };
      if (!(e.key in keys)) return;
      e.preventDefault();
      this.select((keys[e.key] + n) % n, true);
    }

    select(i, focus = false, instant = false) {
      if (i < 0 || i === this.current) return;
      const from = this.panels[this.current];
      const to = this.panels[i];
      const dir = i > this.current ? 1 : -1;
      this.tabs.forEach((t, k) => {
        t.setAttribute('aria-selected', k === i ? 'true' : 'false');
        t.tabIndex = k === i ? 0 : -1;
      });
      if (focus) this.tabs[i].focus({ preventScroll: true });
      this.center(this.tabs[i]);
      this.current = i;
      this.moveInd(!instant);
      this.setBg(to, this.tabs[i], !instant);
      if (from && to) this.swap(from, to, dir, instant);
    }

    // Keep the active tab in view inside the bar, without scrolling the page
    center(tab) {
      const l = this.list;
      if (!l || l.scrollWidth <= l.clientWidth) return;
      l.scrollTo({ left: tab.offsetLeft - (l.clientWidth - tab.offsetWidth) / 2, behavior: reduce() ? 'auto' : 'smooth' });
    }

    // Position the sliding highlight under the active tab (transform only, so it animates on the compositor)
    // Desktop hero background: circle iris. The new image opens as a circle from the clicked tab (clip-path) while it eases
    // from 1.15 to 1 scale inside it; with no image, the current one closes back into the tab. Uses GSAP when the page has
    // it (expo easing), otherwise the Web Animations API. Only clip-path and transform animate; reduced motion is instant.
    ready(url, fn) {
      let im = this.imgCache.get(url);
      if (!im) {
        im = new Image();
        im.src = url;
        this.imgCache.set(url, im);
      }
      if (im.complete && im.naturalWidth) fn();
      else im.addEventListener('load', fn, { once: true });
    }

    setBg(panel, tab, animate = false) {
      const layers = this.bgLayers;
      if (!layers?.length) return;
      const url = (matchMedia('(max-width:989px)').matches && panel?.dataset.bgm) || panel?.dataset.bg || '';
      if (url === this.bgUrl) return;
      this.bgUrl = url;
      this.finishBg?.();

      const hero = this.querySelector('.cy-ac__hero');
      const anim = animate && !reduce() && hero && tab;
      const on = layers.find((l) => l.classList.contains('is-on'));

      // circle centred on the clicked tab, big enough to reach the farthest hero corner
      let c0 = '';
      let c1 = '';
      if (anim) {
        const h = hero.getBoundingClientRect();
        const t = tab.getBoundingClientRect();
        const x = t.left + t.width / 2 - h.left;
        const y = t.top + t.height / 2 - h.top;
        const r = Math.hypot(Math.max(x, h.width - x), Math.max(y, h.height - y)) + 4;
        const at = ' at ' + x + 'px ' + y + 'px)';
        c0 = 'circle(0px' + at;
        c1 = 'circle(' + r + 'px' + at;
      }

      const runs = [];
      const css = (p) => {
        const o = {};
        if (p.clipPath) o.clipPath = p.clipPath;
        if (p.opacity != null) o.opacity = p.opacity;
        if (p.scale != null) o.transform = 'scale(' + p.scale + ')';
        return o;
      };
      const play = (el, from, to, sec, gEase, cEase, done) => {
        const g = window.gsap;
        if (g) {
          const tw = g.fromTo(el, from, Object.assign({}, to, { duration: sec, ease: gEase, clearProps: 'clipPath,transform', onComplete: done || undefined }));
          runs.push(() => tw.progress(1));
        } else {
          const a = el.animate([css(from), css(to)], { duration: sec * 1000, easing: cEase });
          if (done) a.onfinish = done;
          runs.push(() => a.finish());
        }
      };
      this.finishBg = () => {
        runs.splice(0).forEach((f) => f());
        this.finishBg = null;
      };

      // no image for this tab: the current one closes back into the tab, the main hero photo shows
      if (!url) {
        if (!on) return;
        if (!anim) {
          on.classList.remove('is-on');
          return;
        }
        if (lite()) {
          const a = on.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 240, easing: 'ease', fill: 'both' });
          a.onfinish = () => { on.style.opacity = ''; a.cancel(); on.classList.remove('is-on'); };
          runs.push(() => a.finish());
          return;
        }
        play(on, { clipPath: c1 }, { clipPath: c0 }, 0.9, 'expo.inOut', 'cubic-bezier(.87,0,.13,1)', () => on.classList.remove('is-on'));
        return;
      }

      const next = layers.find((l) => l !== on) || layers[0];
      const inner = next.querySelector('.cy-ac__bgi');
      this.ready(url, () => {
        if (this.bgUrl !== url) return;
        inner.style.backgroundImage = 'url("' + url + '")';
        next.style.zIndex = 2;
        if (on) on.style.zIndex = 1;
        if (lite() && anim) next.style.opacity = '0';
        next.classList.add('is-on');
        const hideOld = () => {
          if (on && on !== next) on.classList.remove('is-on');
        };
        if (!anim) {
          hideOld();
          return;
        }
        if (lite()) {
          const a = next.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 320, easing: 'ease', fill: 'both' });
          a.onfinish = () => { next.style.opacity = ''; a.cancel(); hideOld(); };
          runs.push(() => a.finish());
          return;
        }
        play(next, { clipPath: c0 }, { clipPath: c1 }, 1.1, 'expo.inOut', 'cubic-bezier(.87,0,.13,1)', hideOld);
        play(inner, { scale: 1.15 }, { scale: 1 }, 1.5, 'expo.out', 'cubic-bezier(.16,1,.3,1)', null);
      });
    }

    // The label turns white (.is-on) once the highlight has mostly arrived, so it is never white on the light glass.
    moveInd(animate = false) {
      const t = this.tabs?.[this.current];
      if (!this.ind || !t) return;
      this.ind.style.setProperty('--x', t.offsetLeft + 'px');
      this.ind.style.setProperty('--w', t.offsetWidth + 'px');
      clearTimeout(this.onTimer);
      this.tabs.forEach((x) => x.classList.remove('is-on'));
      const on = () => t.classList.add('is-on');
      if (animate && !reduce()) this.onTimer = setTimeout(on, 260);
      else on();
    }

    swap(from, to, dir, instant) {
      this.finish?.();
      to.hidden = false;
      let ended = false;
      const done = () => {
        if (ended) return;
        ended = true;
        from.hidden = true;
        this.finish = null;
      };
      if (instant || reduce()) return done();

      if (lite()) {
        to.style.opacity = '0';
        const a = to.animate(
          [{ opacity: 0, transform: 'translate3d(0,8px,0)' }, { opacity: 1, transform: 'none' }],
          { duration: 280, easing: 'ease', fill: 'both' }
        );
        a.onfinish = () => { to.style.opacity = ''; a.cancel(); done(); };
        this.finish = () => a.finish();
        return;
      }

      const g = window.gsap;
      if (g) {
        const lines = to.querySelectorAll('[data-ac-line]');
        const oldLines = from.querySelectorAll('[data-ac-line]');
        const img = to.querySelector('.cy-ac__shot img');
        const tl = g
          .timeline({
            defaults: { ease: 'expo.out' },
            onComplete: () => {
              done();
              g.set([to, from, img, ...lines, ...oldLines].filter(Boolean), { clearProps: 'clipPath,transform,opacity,zIndex' });
            },
          })
          .set(from, { zIndex: 0 })
          .set(to, { zIndex: 1 })
          // old copy leaves first, so the two titles never share the frame
          .to(oldLines, { y: -14, opacity: 0, duration: 0.3, ease: 'power2.in', stagger: 0.03 }, 0)
          .fromTo(
            to,
            { clipPath: dir > 0 ? 'inset(0% 0% 0% 100% round 24px)' : 'inset(0% 100% 0% 0% round 24px)' },
            { clipPath: 'inset(0% 0% 0% 0% round 24px)', duration: 0.9 },
            0
          );
        if (lines.length) tl.fromTo(lines, { y: 18, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, stagger: 0.06 }, 0.28);
        if (img) tl.fromTo(img, { scale: 1.1 }, { scale: 1, duration: 1.1 }, 0);
        this.finish = () => tl.progress(1);
        return;
      }

      to.classList.toggle('is-back', dir < 0);
      to.classList.add('is-entering');
      const end = () => {
        to.removeEventListener('animationend', onEnd);
        to.classList.remove('is-entering', 'is-back');
        done();
      };
      const onEnd = (e) => e.target === to && end();
      to.addEventListener('animationend', onEnd);
      this.finish = end;
    }

    // One-time entrance. Skipped in the theme editor and with reduced motion, so content is never left hidden.
    reveal() {
      if (reduce() || window.Shopify?.designMode || !('IntersectionObserver' in window)) return;
      this.classList.add('is-armed');
      this.io = new IntersectionObserver(
        ([e]) => {
          if (!e.isIntersecting) return;
          this.io.disconnect();
          this.classList.add('is-in');
        },
        { rootMargin: '0px 0px -15% 0px' }
      );
      this.io.observe(this);
    }
  }

  customElements.define('cy-accessories', CyAccessories);
}
